import React from 'react';
import {
  Box,
  Flex,
  keyframes,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useBrandPalette } from '../../contexts/BrandPaletteContext';
import { spectrumColors } from '../../theme/colors';
import { brandBarMotion } from '../../theme/brandMotion';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.92); }
  50% { opacity: 1; transform: scale(1); }
`;

const barSweep = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;

/** Slow full-pass shine across skeleton blocks */
const shimmerSweep = keyframes`
  0% { transform: translateX(-120%) skewX(-12deg); }
  100% { transform: translateX(140%) skewX(-12deg); }
`;

function useSpectrumLoading() {
  const { paletteId } = useBrandPalette();
  return paletteId === 'spectrum';
}

/**
 * Card / placeholder block with a sweeping shimmer.
 * Spectrum: soft green→blue→purple→orange band; other palettes: light sheen.
 */
export function ShimmerBlock({
  w,
  h,
  minH,
  flex,
  flexShrink,
  mb,
  mt,
  borderRadius = 'md',
  ...props
}) {
  const spectrum = useSpectrumLoading();
  const shine = spectrum
    ? `linear-gradient(100deg, transparent 28%, ${spectrumColors.resources}55 40%, ${spectrumColors.logistics}66 50%, ${spectrumColors.diversity}55 58%, ${spectrumColors.energy}4D 66%, transparent 78%)`
    : 'linear-gradient(100deg, transparent 35%, rgba(255,255,255,0.75) 50%, transparent 65%)';

  return (
    <Box
      position="relative"
      overflow="hidden"
      w={w}
      h={h}
      minH={minH}
      flex={flex}
      flexShrink={flexShrink}
      mb={mb}
      mt={mt}
      borderRadius={borderRadius}
      bg="bg.input"
      {...props}
    >
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        w="55%"
        background={shine}
        animation={`${shimmerSweep} ${spectrum ? '2.6s' : '1.8s'} linear infinite`}
        pointerEvents="none"
      />
    </Box>
  );
}

/** Branded spinner — solid ring for most palettes; spectrum uses 4-stop conic ring. */
export function BrandSpinner({ size = 48, label }) {
  const spectrum = useSpectrumLoading();
  const ring = Math.max(size * 0.12, 3);

  return (
    <VStack spacing={4} role="status" aria-live="polite" aria-busy="true">
      <Box position="relative" w={`${size}px`} h={`${size}px`}>
        {spectrum ? (
          <Box
            position="absolute"
            inset={0}
            borderRadius="full"
            background={`conic-gradient(from 0deg, ${spectrumColors.resources}, ${spectrumColors.logistics}, ${spectrumColors.diversity}, ${spectrumColors.energy}, ${spectrumColors.resources})`}
            animation={`${spin} 1.35s linear infinite`}
            sx={{
              WebkitMask:
                'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3.5px))',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 3.5px))',
            }}
          />
        ) : (
          <>
            <Box
              position="absolute"
              inset={0}
              borderRadius="full"
              border={`${ring}px solid`}
              borderColor="bg.amberTint"
            />
            <Box
              position="absolute"
              inset={0}
              borderRadius="full"
              border={`${ring}px solid transparent`}
              borderTopColor="utkarsh.amber"
              borderRightColor="utkarsh.amber"
              animation={`${spin} 0.85s linear infinite`}
            />
          </>
        )}
        <Flex
          position="absolute"
          inset="18%"
          align="center"
          justify="center"
          borderRadius="full"
          bg={spectrum ? 'bg.surface' : 'bg.amberTint'}
          borderWidth={spectrum ? '1px' : 0}
          borderColor={spectrum ? 'border.subtle' : 'transparent'}
          animation={`${pulse} 1.6s ease-in-out infinite`}
        >
          <Text
            fontSize={`${Math.max(size * 0.28, 12)}px`}
            fontWeight="800"
            color="text.primary"
            letterSpacing="-0.02em"
            lineHeight="1"
          >
            U
          </Text>
        </Flex>
      </Box>
      {label ? (
        <Text
          fontSize="16px"
          fontWeight="600"
          color="text.secondary"
          letterSpacing="0.01em"
        >
          {label}
        </Text>
      ) : null}
    </VStack>
  );
}

/** Indeterminate progress bar — spectrum uses the brand gradient. */
export function BrandProgressBar({ w = '180px', h = '4px' }) {
  const spectrum = useSpectrumLoading();
  return (
    <Box
      w={w}
      h={h}
      borderRadius="full"
      overflow="hidden"
      bg="bg.input"
      position="relative"
    >
      {spectrum ? (
        <Box
          position="absolute"
          inset={0}
          bgImage={spectrumColors.brandBarGradient}
          opacity={0.35}
        />
      ) : null}
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        w="42%"
        borderRadius="full"
        bg={spectrum ? undefined : 'utkarsh.amber'}
        bgImage={spectrum ? spectrumColors.buttonGradient : undefined}
        animation={`${barSweep} ${spectrum ? '2.4s' : '1.2s'} ease-in-out infinite`}
      />
    </Box>
  );
}

/** Full-area page / detail loader. */
export function PageLoader({
  label = 'Loading…',
  minH = '420px',
  py = 20,
  ...props
}) {
  return (
    <Flex
      justify="center"
      align="center"
      minH={minH}
      py={py}
      w="100%"
      {...props}
    >
      <VStack spacing={5}>
        <BrandSpinner size={72} label={label} />
        <BrandProgressBar w="160px" />
      </VStack>
    </Flex>
  );
}

/** Full-viewport loader while auth / bootstrap resolves. */
export function AuthLoader({ label = 'Loading Utkarsh…' }) {
  const spectrum = useSpectrumLoading();
  return (
    <Flex
      minH="100vh"
      w="100%"
      align="center"
      justify="center"
      bg="bg.canvas"
      px={6}
    >
      <VStack spacing={7}>
        {spectrum ? (
          <Box
            h="4px"
            w="120px"
            borderRadius="full"
            bgImage={spectrumColors.brandBarGradient}
            mb={-2}
          />
        ) : null}
        <BrandSpinner size={88} />
        <Box textAlign="center">
          <Text fontSize="22px" fontWeight="700" color="text.primary">
            Utkarsh Admin
          </Text>
          <Text mt={2} fontSize="16px" color="text.secondary">
            {label}
          </Text>
        </Box>
        <BrandProgressBar w="200px" h="5px" />
      </VStack>
    </Flex>
  );
}

/** Skeleton placeholder for list / table cards. */
export function TableLoader({ rows = 6, columns = 5, showHeader = true }) {
  return (
    <Box px={{ base: 5, xl: 6 }} py={{ base: 6, xl: 8 }} minH="320px">
      {showHeader ? <ShimmerBlock h="18px" w="160px" mb={6} /> : null}
      <VStack spacing={4} align="stretch">
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <Flex
            key={rowIdx}
            gap={5}
            align="center"
            py={4}
            px={4}
            borderRadius="md"
            bg={rowIdx % 2 === 0 ? 'bg.surface' : 'transparent'}
          >
            {Array.from({ length: columns }).map((__, colIdx) => (
              <ShimmerBlock
                key={colIdx}
                flex={colIdx === 1 ? 1.4 : 1}
                h="16px"
              />
            ))}
          </Flex>
        ))}
      </VStack>
    </Box>
  );
}

/** Skeleton cards for dashboard / summary grids. */
export function DashboardLoader({ cards = 3 }) {
  return (
    <Box>
      <ShimmerBlock h="32px" w="180px" mb={2} />
      <ShimmerBlock h="14px" w="360px" maxW="100%" mb={6} />
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {Array.from({ length: cards }).map((_, i) => (
          <Box
            key={i}
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            p={5}
          >
            <ShimmerBlock h="12px" w="90px" mb={4} />
            <ShimmerBlock h="36px" w="72px" mb={3} />
            <ShimmerBlock h="20px" w="100px" borderRadius="full" />
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

/** Hub-style metric card skeletons (At a glance) with shimmer. */
export function HubLoader({ cards = 4, showSpinner = true }) {
  const spectrum = useSpectrumLoading();
  return (
    <Box w="100%" role="status" aria-busy="true" aria-label="Loading Hub">
      {spectrum ? (
        <Box
          h="3px"
          w="100%"
          maxW="280px"
          borderRadius="full"
          mb={4}
          bgImage={spectrumColors.brandBarGradient}
          sx={brandBarMotion('8s')}
        />
      ) : null}
      <ShimmerBlock h="14px" w="100px" mb={3} />
      <SimpleGrid columns={{ base: 1, sm: 2, lg: cards }} spacing={3}>
        {Array.from({ length: cards }).map((_, i) => (
          <Box
            key={i}
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="12px"
            px={3.5}
            py={3}
            overflow="hidden"
          >
            <Flex align="center" gap={3} mb={3}>
              <ShimmerBlock
                w="44px"
                h="44px"
                borderRadius="10px"
                flexShrink={0}
              />
              <Box flex="1" minW={0}>
                <ShimmerBlock h="10px" w="72px" mb={2} />
                <ShimmerBlock h="28px" w="56px" />
              </Box>
              <ShimmerBlock
                w="64px"
                h="56px"
                borderRadius="10px"
                flexShrink={0}
                bg="bg.secondaryTint"
              />
            </Flex>
            <Flex gap={3} pt={2} borderTopWidth="1px" borderColor="border.subtle">
              <ShimmerBlock flex={1} h="32px" />
              <ShimmerBlock flex={1} h="32px" />
              <ShimmerBlock flex={1} h="32px" />
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
      {showSpinner ? (
        <Flex justify="center" mt={8}>
          <BrandSpinner size={48} label="Loading Hub…" />
        </Flex>
      ) : null}
    </Box>
  );
}

/** Compact inline loader (filters refresh, small panels). */
export function InlineLoader({ label = 'Loading…', py = 12 }) {
  return (
    <Flex justify="center" align="center" py={py} w="100%" minH="180px">
      <BrandSpinner size={56} label={label} />
    </Flex>
  );
}
